/**
 * Charger → product-media matcher. Maps a charger to the store's product
 * media family so charger surfaces (My Chargers list, charger detail,
 * My Home) can show the model's ambient hero loop instead of a glyph.
 *
 * Matching is deliberately conservative: an identity override map wins
 * (for units whose OCPP vendor/model strings don't describe the hardware
 * — Warren's Weeyu presents as the Autel wallbox), then vendor/model
 * heuristics; anything unmatched returns null and the caller keeps its
 * existing glyph rendering exactly.
 */

export type ChargerMediaKey = 'wallbox' | 'pedestal' | 'dcfc';

export interface ChargerMedia {
  loop: string;
  poster: string;
}

const MEDIA: Record<ChargerMediaKey, ChargerMedia> = {
  wallbox: {
    loop: '/media/products/wallbox/hero-loop.mp4',
    poster: '/media/products/wallbox/hero-loop-poster.jpg',
  },
  pedestal: {
    loop: '/media/products/pedestal/hero-loop.mp4',
    poster: '/media/products/pedestal/hero-loop-poster.jpg',
  },
  dcfc: {
    loop: '/media/products/dcfc/hero-loop.mp4',
    poster: '/media/products/dcfc/hero-loop-poster.jpg',
  },
};

/** Units whose OCPP identity is known but whose vendor/model strings
 *  don't describe the hardware. Seeded with Warren's home unit. */
const IDENTITY_OVERRIDES: Record<string, ChargerMediaKey> = {
  CU89TVWKPVJ8: 'wallbox',
};

export function mediaKeyForCharger(charger: {
  identity: string;
  vendor?: string | null;
  model?: string | null;
}): ChargerMediaKey | null {
  const override = IDENTITY_OVERRIDES[charger.identity];
  if (override) return override;

  const haystack = `${charger.vendor ?? ''} ${charger.model ?? ''}`.toLowerCase();
  if (!haystack.trim()) return null;
  // AC Ultra is the pedestal family; check before the generic AC match.
  if (haystack.includes('ultra')) return 'pedestal';
  // Autel DC hardware (DC Compact / MaxiCharger DC).
  if (/\bdc\b|dc compact|maxicharger dc/.test(haystack)) return 'dcfc';
  // Autel AC wallbox family (MaxiCharger AC / Elite / Pro / home units).
  if (haystack.includes('autel')
    && /maxicharger|elite|pro\b|wallbox|\bac\b|home/.test(haystack)) {
    return 'wallbox';
  }
  return null;
}

export function mediaForCharger(charger: {
  identity: string;
  vendor?: string | null;
  model?: string | null;
}): ChargerMedia | null {
  const key = mediaKeyForCharger(charger);
  return key ? MEDIA[key] : null;
}
